/**
 * LOS AJUSTES EN VIVO
 * =============================================================================
 * Lo que se cambia en el panel se ve en la página. Sin tocar código y sin volver
 * a publicar.
 *
 * -----------------------------------------------------------------------------
 * POR QUÉ HACÍA FALTA
 * -----------------------------------------------------------------------------
 * Antes la página leía sus datos de `src/config.ts`, que es un archivo del código.
 * Ese archivo se compila DENTRO de la página, así que para cambiar el Instagram
 * había que editar el archivo, compilar y publicar otra vez.
 *
 * El panel guardaba los ajustes en su base de datos… y la página nunca los leía.
 * Por eso no cambiaba nada.
 *
 * -----------------------------------------------------------------------------
 * CÓMO FUNCIONA AHORA
 * -----------------------------------------------------------------------------
 * Al abrir la página se piden los ajustes al servidor y se guardan aquí. Todos los
 * componentes leen de aquí, así que el cambio del panel llega a todas partes.
 *
 * -----------------------------------------------------------------------------
 * SI EL SERVIDOR NO CONTESTA
 * -----------------------------------------------------------------------------
 * Se usan los valores de `src/config.ts`, que siguen ahí como respaldo. Así la
 * página NUNCA se queda sin redes, sin teléfono ni sin dirección porque el
 * servidor esté caído o alguien no tenga internet.
 *
 * Es lo correcto: el respaldo es lo que ya funcionaba, y el panel solo lo mejora.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { CONFIG } from '../config';
import { useIdioma } from '../i18n/useIdioma';

/**
 * Los valores de respaldo, sacados de `src/config.ts`.
 *
 * Si el servidor no contesta, la página usa estos. Son los mismos que había antes
 * de que existiera el panel.
 */
function respaldo(): Record<string, string> {
  return {
    academia_nombre: CONFIG.academia.nombre,
    academia_lema: CONFIG.academia.lema,
    academia_firma: CONFIG.academia.firma,

    contacto_correo: CONFIG.correo,
    contacto_telefono: '',
    contacto_direccion: CONFIG.ubicacion.calle,
    contacto_ciudad: CONFIG.ubicacion.ciudad,
    contacto_pais_es: CONFIG.ubicacion.pais.es,
    contacto_pais_en: CONFIG.ubicacion.pais.en,
    contacto_mapa: CONFIG.ubicacion.mapa,

    red_instagram: CONFIG.redes.instagram,
    red_facebook: CONFIG.redes.facebook,
    red_tiktok: CONFIG.redes.tiktok,

    whatsapp_activo: CONFIG.whatsapp.activo ? '1' : '0',
    whatsapp_numero: CONFIG.whatsapp.numero,
    whatsapp_mensaje: CONFIG.whatsapp.mensaje,

    avisos_activos: '0',
    avisos_titulo: 'M Soccer Academy',
    avisos_invitacion: '',

    horarios_confirmados: CONFIG.horarios.confirmados ? '1' : '0',
    horarios_nota_es: CONFIG.horarios.nota.es,
    horarios_nota_en: CONFIG.horarios.nota.en,

    galeria_activa: '1',
    galeria_titulo_es: '',
    galeria_titulo_en: '',
  };
}

interface ContextoAjustes {
  /** El valor de un ajuste. */
  valor: (clave: string, porDefecto?: string) => string;
  /** Si un ajuste de sí o no está encendido. */
  activo: (clave: string) => boolean;
  /** Si ya se pidieron al servidor. */
  listo: boolean;
}

const AjustesContexto = createContext<ContextoAjustes | null>(null);

export function ProveedorAjustes({ children }: { children: ReactNode }) {
  const [valores, setValores] = useState<Record<string, string>>(() => respaldo());
  const [listo, setListo] = useState(false);

  useEffect(() => {
    let vivo = true;

    fetch(CONFIG.ajustes.api)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!vivo || !d?.valores) return;
        /* Se mezclan con el respaldo: si el servidor devuelve una clave vacía, se
           queda el valor de respaldo en lugar de borrarlo. Así un ajuste mal puesto
           no puede dejar la página sin teléfono ni sin dirección. */
        setValores((antes) => {
          const salida = { ...antes };
          for (const [k, v] of Object.entries(d.valores as Record<string, string>)) {
            if (v !== '' && v !== null && v !== undefined) salida[k] = String(v);
          }
          return salida;
        });
      })
      .catch(() => {
        // Sin servidor se queda el respaldo, que ya está puesto.
      })
      .finally(() => {
        if (vivo) setListo(true);
      });

    return () => {
      vivo = false;
    };
  }, []);

  const api = useMemo<ContextoAjustes>(
    () => ({
      valor: (clave, porDefecto = '') => valores[clave] ?? porDefecto,
      activo: (clave) => valores[clave] === '1' || valores[clave] === 'true',
      listo,
    }),
    [valores, listo]
  );

  return <AjustesContexto.Provider value={api}>{children}</AjustesContexto.Provider>;
}

/**
 * Lee los ajustes.
 *
 * Si se usa fuera del proveedor devuelve los de respaldo, en lugar de reventar.
 * Así un componente suelto nunca rompe la página entera.
 */
export function useAjustes(): ContextoAjustes {
  const ctx = useContext(AjustesContexto);
  if (ctx) return ctx;

  const r = respaldo();
  return {
    valor: (clave, porDefecto = '') => r[clave] ?? porDefecto,
    activo: (clave) => r[clave] === '1',
    listo: false,
  };
}

/**
 * El teléfono de WhatsApp, ya armado como enlace.
 *
 * Devuelve `null` si está apagado o si no hay número. Así quien lo use sabe que no
 * hay que enseñar nada, en lugar de tener que comprobarlo por su cuenta.
 */
export function enlaceWhatsApp(
  activo: boolean,
  numero: string,
  mensaje: string
): string | null {
  if (!activo || !numero) return null;
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`;
}

/** El país, en el idioma que se esté viendo. */
export function usePais(): string {
  const { valor } = useAjustes();
  const { idioma } = useIdioma();
  return valor(idioma === 'en' ? 'contacto_pais_en' : 'contacto_pais_es');
}
